/**
 * TripOverlay - Trip detail overlay matching designer's TripPage layout.
 *
 * Supports two variants:
 * - 'fullscreen': Floating UI elements positioned over the full viewport (designer pattern)
 * - 'window': Contained in a draggable OverlayContainer window
 *
 * Layout:
 * - SearchBar: Top center
 * - ChatPanel: Right side
 * - ScheduleDrawer: Bottom (collapsible)
 */

import { useCallback, useState, type ReactNode } from 'react'
import { OverlayContainer } from '@/overlays/shared'
import { HippoProvider } from '@/components/ui'
import { SearchBar } from './SearchBar'
import { ChatPanel } from './ChatPanel'
import { ScheduleDrawer } from './ScheduleDrawer'

import type { Trip as TripMessage, Place } from '@generated/proto/model'

interface TripOverlayProps {
  trip: TripMessage
  tripId: string
  /** Layout variant: 'fullscreen' for globe page, 'window' for overlay container */
  variant?: 'fullscreen' | 'window'
  /** Called when place is selected from search */
  onPlaceSelect?: (place: Place) => void
  /** Called when close button clicked (window variant only) */
  onClose?: () => void
  /** Optional children to render instead of default components */
  children?: ReactNode
}

export function TripOverlay({
  trip,
  tripId,
  variant = 'fullscreen',
  onPlaceSelect,
  onClose,
  children,
}: TripOverlayProps) {
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null)

  const handlePlaceSelect = useCallback((place: Place) => {
    setSelectedPlace(place)
    onPlaceSelect?.(place)
    if (import.meta.env.DEV) {
      console.log('Place selected:', place)
    }
  }, [onPlaceSelect])

  // Default content if no children provided
  const content = children ?? (
    <>
      {/* SearchBar - Top center */}
      {variant === 'fullscreen' ? (
        <SearchBar onPlaceSelect={handlePlaceSelect} />
      ) : (
        <div className="absolute top-3 left-1/2 transform -translate-x-1/2 z-40">
          <SearchBar onPlaceSelect={handlePlaceSelect} />
        </div>
      )}

      {/* ChatPanel - Right side */}
      {variant === 'fullscreen' ? (
        <ChatPanel tripId={tripId} />
      ) : (
        <div className="absolute right-2 top-14 bottom-24 w-[280px] z-30">
          <ChatPanel tripId={tripId} />
        </div>
      )}

      {/* ScheduleDrawer - Bottom */}
      {variant === 'fullscreen' ? (
        <ScheduleDrawer trip={trip} tripId={tripId} />
      ) : (
        <div className="absolute bottom-0 left-0 right-0 z-20">
          <ScheduleDrawer trip={trip} tripId={tripId} />
        </div>
      )}
    </>
  )

  // Fullscreen variant: just render content with HippoProvider
  if (variant === 'fullscreen') {
    return (
      <HippoProvider initialPersonalityLevel="default">
        {content}
      </HippoProvider>
    )
  }

  // Window variant: wrap in OverlayContainer
  return (
    <OverlayContainer
      title={trip.title || 'Trip Details'}
      minWidth={700}
      maxWidth={900}
      initialPosition={{ x: 40, y: 40 }}
      onClose={onClose}
    >
      <HippoProvider initialPersonalityLevel="default">
        <div className="relative h-[70vh] bg-[#0b1b2b] rounded-b-md overflow-hidden">
          {/* Vignette effect */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(circle at center, transparent 0%, transparent 60%, rgba(0, 0, 0, 0.3) 85%, rgba(0, 0, 0, 0.5) 100%)'
            }}
          />
          {content}
        </div>
      </HippoProvider>
    </OverlayContainer>
  )
}
